/* Feratia — pre-order presentation sync (theme-native, Broadcast 7).
 *
 * Liquid renders the correct initial state for the selected variant; this
 * keeps it correct after that. Broadcast does not re-render the buy box on a
 * variant change — it dispatches a bubbling `theme:variant:change` with
 * `detail.variant` — so on that event this:
 *   1. swaps the per-variant batch panel / sold-out teaser,
 *   2. relabels every add-to-cart button in scope (Broadcast's own label
 *      logic keys off a product-wide `[data-product-preorder]` marker cached
 *      at init, which is never set for app-driven pre-orders),
 *   3. rewrites the three hidden line-item property inputs in the form,
 *   4. updates the assurance line, the sticky cart bar line, and re-anchors
 *      the today-anchored promises (delivery countdown, "4–6 Days Delivery").
 *
 * State comes from the per-variant registry Liquid emits
 * (`<script type="application/json" data-feratia-po="<variant id>">`),
 * which is itself read from the app-owned `preorder.batches` metafield. No
 * entry = normal purchase. Purchasability is never touched: a variant whose
 * `available` is false keeps the theme's sold-out button (the app closes
 * intake through inventory policy).
 *
 * A `fetch` interceptor also merges the properties into JSON-body adds
 * (`bundle-box.js`, `hc-discount-gift.js`) so every add path carries the
 * same trio exactly once — see preorder-line-properties-standard.md. */
(function () {
  'use strict';

  var PROPS = { preorder: 'properties[_preorder]', batch: 'properties[_preorder_batch]', eta: 'properties[_preorder_eta]' };
  var cache = {};

  /* ---- registry -------------------------------------------------------- */
  function entry(id) {
    id = String(id || '');
    if (!id) return null;
    var node = document.querySelector('script[data-feratia-po="' + id + '"]');
    if (!node) return null;
    if (cache[id] && cache[id].node === node) return cache[id].data;
    var data = null;
    try { data = JSON.parse(node.textContent); } catch (e) { data = null; }
    cache[id] = { node: node, data: data };
    return data;
  }

  function isPreorder(e, available) {
    return !!e && e.mode === 'preorder' && available !== false;
  }

  /* Walk up to the buy-box container that owns the panels; a quick-add modal
   * opened over a product page puts a second buy box on the same document,
   * and scoping to `document` would relabel both from whichever changed. */
  function scopeOf(node) {
    var el = node && node.nodeType === 1 ? node : null;
    var container = el && el.closest && el.closest('[data-section-type], [data-quick-add-modal]');
    return container || document;
  }

  function strings() {
    var s = (window.theme && window.theme.strings) || {};
    return { add: s.addToCart || 'Add to cart' };
  }

  /* ---- pieces ---------------------------------------------------------- */
  function swapPanels(scope, id) {
    scope.querySelectorAll('[data-feratia-po-panel]').forEach(function (p) {
      p.hidden = p.getAttribute('data-feratia-po-panel') !== id;
    });
    scope.querySelectorAll('[data-feratia-po-soldout]').forEach(function (p) {
      p.hidden = p.getAttribute('data-feratia-po-soldout') !== id;
    });
  }

  /* Swap only the verb; keep the price span the theme appends when
   * "show price on button" is on. Restore the theme's label when leaving a
   * pre-order variant — the theme has usually done that already, but the
   * rAF re-apply below must not undo it. Never touch a disabled (sold-out)
   * button: that label is the theme's, always. */
  function relabel(scope, pre, label) {
    scope.querySelectorAll('[data-add-to-cart-text]').forEach(function (el) {
      var btn = el.closest('button');
      if (btn && btn.disabled) { delete el.dataset.fpoLabeled; return; }
      if (pre) {
        setVerb(el, label);
        el.dataset.fpoLabeled = label;
      } else if (el.dataset.fpoLabeled) {
        // Only undo our own verb; if the theme already rewrote the label
        // (or its tag-based pre-order applies), leave it alone.
        if (currentVerb(el) === el.dataset.fpoLabeled) setVerb(el, strings().add);
        delete el.dataset.fpoLabeled;
      }
    });
  }

  function currentVerb(el) {
    var price = el.querySelector('.btn__price');
    var current = price ? el.firstChild && el.firstChild.textContent : el.textContent;
    return (current || '').trim();
  }

  function setVerb(el, verb) {
    var price = el.querySelector('.btn__price');
    if (currentVerb(el) === verb) return;
    if (price) {
      el.textContent = verb + ' ';
      el.appendChild(price);
    } else {
      el.textContent = verb;
    }
  }

  function setInput(form, key, marker, value) {
    var input = form.querySelector('[data-feratia-po-prop="' + marker + '"]');
    if (value == null || value === '') {
      if (input) input.remove();
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.setAttribute('data-feratia-po-prop', marker);
      form.appendChild(input);
    }
    input.value = value;
  }

  function syncProps(scope, pre, e) {
    scope.querySelectorAll('form[data-product-form]').forEach(function (form) {
      setInput(form, PROPS.preorder, 'preorder', pre ? 'true' : null);
      setInput(form, PROPS.batch, 'batch', pre ? e.code : null);
      setInput(form, PROPS.eta, 'eta', pre && e.eta ? e.eta : null);
    });
  }

  function syncAssure(scope, pre, e) {
    scope.querySelectorAll('[data-feratia-po-assure]').forEach(function (el) {
      if (pre && e.assure) { el.textContent = e.assure; el.hidden = false; }
      else el.hidden = true;
    });
  }

  /* Sticky cart bar: the "order within … delivered by <date>" countdown is
   * a today-anchored promise that is false for a pre-order; show the ship
   * window line instead. */
  function syncCartBar(scope, pre, e) {
    scope.querySelectorAll('[data-cart-bar-delivery]').forEach(function (el) {
      el.classList.toggle('fpo-hide', pre);
    });
    scope.querySelectorAll('[data-feratia-po-bar]').forEach(function (el) {
      var text = el.querySelector('[data-feratia-po-bar-text]') || el;
      if (pre && e.bar) { text.textContent = e.bar; el.hidden = false; }
      else el.hidden = true;
    });
  }

  /* Icon row / trust line: "4-6 Days Delivery" is editor copy that becomes a
   * false claim on a pre-order. Re-anchor the icon text to the ship window
   * (same icon, honest date) and hide the near-ATC trust line; restore the
   * original copy when the shopper switches back to a stocked variant. */
  var DELIVERY_RE = /deliver/i;
  function syncPromises(scope, pre, e) {
    scope.querySelectorAll('.block__icon__text > *').forEach(function (el) {
      if (!el.dataset.fpoOriginal) {
        if (!DELIVERY_RE.test(el.textContent)) return;
        el.dataset.fpoOriginal = el.innerHTML;
      }
      if (pre) {
        var strong = el.querySelector('strong');
        var copy = e.etaLong ? 'Ships ' + e.etaLong : 'Pre-order';
        if (strong) { strong.textContent = copy; }
        else el.textContent = copy;
      } else if (el.innerHTML !== el.dataset.fpoOriginal) {
        el.innerHTML = el.dataset.fpoOriginal;
      }
    });
    scope.querySelectorAll('.mcd-near-trust').forEach(function (el) {
      if (DELIVERY_RE.test(el.textContent)) el.classList.toggle('fpo-hide', pre);
    });
    // Anything Liquid marked as a today-anchored promise (e.g. the
    // shipping-options block's "Get it by" dates).
    scope.querySelectorAll('[data-feratia-po-hide]').forEach(function (el) {
      el.classList.toggle('fpo-hide', pre);
    });
  }

  function apply(scope, variant) {
    // No variant (unavailable option combination): show nothing pre-order.
    if (!variant) variant = { id: '', available: false };
    var id = String(variant.id);
    var e = entry(id);
    var pre = isPreorder(e, variant.available);
    swapPanels(scope, id);
    relabel(scope, pre, pre ? e.label : '');
    syncProps(scope, pre, e || {});
    syncAssure(scope, pre, e || {});
    syncCartBar(scope, pre, e || {});
    syncPromises(scope, pre, e || {});
  }

  /* ---- events ---------------------------------------------------------- */
  document.addEventListener('theme:variant:change', function (ev) {
    var variant = ev.detail && ev.detail.variant;
    var scope = scopeOf(ev.target);
    apply(scope, variant);
    // Broadcast writes its own label around the same time; re-apply a frame
    // later so the last word is always the per-variant one.
    window.requestAnimationFrame(function () { apply(scope, variant); });
  });

  /* Initial pass per buy box: Liquid already rendered the right state, but
   * the theme's init may have rewritten the button label from its cached
   * product-wide marker. Read the selected id from the form. */
  function initRoot(root) {
    if (root.dataset.fpoInit) return;
    root.dataset.fpoInit = '1';
    var scope = scopeOf(root);
    var form = scope.querySelector('form[data-product-form]');
    // `form.elements` includes inputs associated via the form= attribute (this
    // theme keeps name="id" outside the <form>); note `form.id` itself is
    // shadowed by that very input, so never use it for the element id.
    var idInput = form && form.elements.namedItem('id');
    if (!idInput || !idInput.value) return;
    var btn = form.querySelector('[data-add-to-cart]');
    apply(scope, { id: idInput.value, available: !(btn && btn.disabled) });
  }

  function initAll() {
    document.querySelectorAll('[data-feratia-po-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // Quick-add modals and AJAX sections arrive later.
  new MutationObserver(function () { initAll(); }).observe(document.documentElement, { childList: true, subtree: true });

  /* ---- JSON add paths --------------------------------------------------- */
  function decorateItem(item) {
    if (!item || typeof item !== 'object') return item;
    var props = item.properties || {};
    if (props._preorder) return item;
    var e = entry(item.id);
    if (!isPreorder(e)) return item;
    props._preorder = 'true';
    props._preorder_batch = e.code;
    if (e.eta) props._preorder_eta = e.eta;
    item.properties = props;
    return item;
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (init && typeof init.body === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url)) {
          var body = JSON.parse(init.body);
          var before = init.body;
          if (Array.isArray(body.items)) body.items = body.items.map(decorateItem);
          else if (body.id) body = decorateItem(body);
          var after = JSON.stringify(body);
          if (after !== before) init = Object.assign({}, init, { body: after });
        }
      } catch (e) { /* not JSON, or not ours — send untouched */ }
      return nativeFetch.call(this, input, init);
    };
  }
})();
